import React, { useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ConfigProvider, Spin } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import theme from './config/theme';

import AuthLayout from './layouts/AuthLayout';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import WeChatCallbackPage from './pages/auth/WeChatCallbackPage';
import WeChatBindPhonePage from './pages/auth/WeChatBindPhonePage';

// Dashboard pages
 

// Debt Management pages
import DebtManagementPage from './pages/DebtManagement/DebtManagementPage';
import PublishDebtPage from './pages/DebtManagement/PublishDebtPage';
import MyDebtsPage from './pages/DebtManagement/MyDebtsPage';
import MyDebtIntentsPage from './pages/DebtManagement/MyDebtIntentsPage';
import DebtDraftsPage from './pages/DebtManagement/DebtDraftsPage';
import DebtFollowupPage from './pages/DebtManagement/DebtFollowupPage';
import BatchPublishPage from './pages/DebtManagement/BatchPublishPage';
import DebtDetailPage from './pages/DebtManagement/DebtDetailPage';

// Debt Marketplace pages
import BrowseDebtsPage from './pages/DebtMarketplace/BrowseDebtsPage';
import FavoritesPage from './pages/DebtMarketplace/FavoritesPage';

// Debt Notice pages
import PublishNoticeSelectDebtPage from './pages/DebtNotice/PublishNoticeSelectDebtPage';
import PublishNoticeChooseTemplatePage from './pages/DebtNotice/PublishNoticeChooseTemplatePage';
import PublishNoticeEditContentPage from './pages/DebtNotice/PublishNoticeEditContentPage';
import PublishNoticeSignaturePage from './pages/DebtNotice/PublishNoticeSignaturePage';
import PublishNoticeGeneratePdfPage from './pages/DebtNotice/PublishNoticeGeneratePdfPage';
import PublishNoticeSendPage from './pages/DebtNotice/PublishNoticeSendPage';
import PublishNoticeArchivePage from './pages/DebtNotice/PublishNoticeArchivePage';
import NoticeHistoryPage from './pages/DebtNotice/NoticeHistoryPage';
import NoticeTemplatePage from './pages/DebtNotice/NoticeTemplatePage';
import SealManagementPage from './pages/Profile/SealManagementPage';

// Profile pages
import IdentityVerificationPage from './pages/Profile/IdentityVerificationPage';
import AccountSettingsPage from './pages/Profile/AccountSettingsPage';

// Wallet pages
import WalletPage from './pages/Wallet/WalletPage';
import RechargePage from './pages/Recharge/RechargePage';
import ConsumptionPage from './pages/Consumption/ConsumptionPage';

// Statistics pages
import StatisticsPage from './pages/Statistics/StatisticsPage';
import TransactionStatsPage from './pages/Statistics/TransactionStatsPage';
import ProfitAnalysisPage from './pages/Statistics/ProfitAnalysisPage';
import MarketTrendsPage from './pages/Statistics/MarketTrendsPage';

import DebtSharePage from './pages/share/DebtSharePage';
import NoticeLetterSharePage from './pages/share/NoticeBatchSharePage';
import NoticeLetterCodePage from './pages/share/NoticeLetterCodePage';

import { getUserInfoAsync, restoreToken } from './redux/slices/authSlice';
import { TokenManager } from './services/api';
import ProtectedRoute from './components/auth/ProtectedRoute';

import './App.css';
import './styles/index.css';

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated, initialized } = useSelector(state => state.auth);

  const shouldInitAuth = useMemo(() => {
    const pathname = location?.pathname || '/';
    // 仅对“需要登录态的主站页面”做自动登录态恢复：
    // - /auth/**：登录/注册页本身不需要预先拉用户信息，避免产生误导性提示
    // - /notice-letter、/share/**：公示/分享等公开页面不依赖登录态，避免无意义请求
    if (pathname.startsWith('/auth')) return false;
    if (pathname === '/notice-letter') return false;
    if (pathname.startsWith('/share/')) return false;
    return true;
  }, [location?.pathname]);

  useEffect(() => {
    // 全局登录态初始化：在 JWT/Cookie + stateless 模式下，刷新页面后前端需要通过一次 /api/user/current
    // 来"恢复"登录态（是否携带有效 Cookie），并据此决定渲染主站还是跳转登录页。
    if (!initialized && shouldInitAuth) {
      // 先从 storage 恢复 token 到 Redux state
      dispatch(restoreToken());
      // 再通过 API 验证并获取用户信息
      dispatch(getUserInfoAsync());
    }
  }, [dispatch, initialized, shouldInitAuth]);

  // 多标签页 Token 同步监听
  useEffect(() => {
    const handleStorageChange = (event) => {
      // 监听 localStorage 中 auth_token 的变化
      if (event.key === 'auth_token') {
        const newToken = event.newValue;
        if (newToken) {
          // 其他标签页设置了 token，同步到当前标签页
          sessionStorage.setItem('auth_token', newToken);
          dispatch(restoreToken());
        } else {
          // 其他标签页清除了 token，同步到当前标签页
          sessionStorage.removeItem('auth_token');
          dispatch({ type: 'auth/logout' });
        }
      }
    };

    // 监听 storage 事件（跨标签页通信）
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [dispatch]);

  const fullPageLoading = (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      {/* 初始化阶段统一展示全屏加载，避免出现“先跳登录页又跳回首页”的闪烁 */}
      <Spin size="large" />
    </div>
  );

  return (
    <ConfigProvider 
      theme={theme} 
      locale={zhCN}
      componentSize="middle"
    >
      <div className="App">
        <Routes>
        <Route path="/notice-letter" element={<NoticeLetterCodePage />} />
        <Route path="/share/debt/:token" element={<DebtSharePage />} />
        <Route path="/share/notice-letter/:token" element={<NoticeLetterSharePage />} />

        {/* 认证相关路由 */}
        <Route path="/auth" element={<AuthLayout />}>
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="wechat/callback" element={<WeChatCallbackPage />} />
          <Route path="wechat/bind" element={<WeChatBindPhonePage />} />
          <Route index element={<Navigate to="login" replace />} />
        </Route>

        {/* 主应用路由 */}
        <Route path="/" element={
          !initialized ? fullPageLoading : (isAuthenticated ? <MainLayout /> : <Navigate to="/auth/login" replace />)
        }>
          <Route index element={<Navigate to="/debt-marketplace/browse" replace />} />

          {/* 债权管理 */}
          <Route path="debt-management" element={<DebtManagementPage />} />
          <Route path="debt-management/publish" element={<PublishDebtPage />} />
          <Route path="debt-management/my-debts" element={<MyDebtsPage />} />
          <Route path="debt-management/intents" element={<MyDebtIntentsPage />} />
          <Route path="debt-management/drafts" element={<DebtDraftsPage />} />
          <Route path="debt-management/followup" element={<DebtFollowupPage />} />
          <Route path="debt-management/detail/:id" element={<DebtDetailPage />} />
          <Route path="debt-management/batch-publish" element={<BatchPublishPage />} />

          {/* 债权广场 */}
          <Route path="debt-marketplace" element={<Navigate to="/debt-marketplace/browse" replace />} />
          <Route path="debt-marketplace/browse" element={<BrowseDebtsPage />} />
          <Route path="debt-marketplace/favorites" element={<FavoritesPage />} />

          {/* 债权催告 */}
          <Route path="debt-notice" element={<Navigate to="/debt-notice/publish" replace />} />
          <Route path="debt-notice/publish" element={<PublishNoticeSelectDebtPage />} />
          <Route path="debt-notice/publish/choose-template/:id" element={<PublishNoticeChooseTemplatePage />} />
          <Route path="debt-notice/publish/edit-content/:id" element={<PublishNoticeEditContentPage />} />
          <Route path="debt-notice/publish/signature/:id" element={<PublishNoticeSignaturePage />} />
          <Route path="debt-notice/publish/generate-pdf/:id" element={<PublishNoticeGeneratePdfPage />} />
          <Route path="debt-notice/publish/send/:id" element={<PublishNoticeSendPage />} />
          <Route path="debt-notice/publish/archive/:id" element={<PublishNoticeArchivePage />} />
          <Route path="debt-notice/history" element={<NoticeHistoryPage />} />
          <Route path="debt-notice/template" element={<NoticeTemplatePage />} />

          {/* 个人中心 */}
          <Route path="profile" element={<AccountSettingsPage />} />
          <Route path="profile/identity-verification" element={<IdentityVerificationPage />} />
          <Route path="profile/account-settings" element={<AccountSettingsPage />} />
          <Route path="profile/seal-management" element={<SealManagementPage />} />

          {/* 钱包充值 */}
          <Route path="wallet" element={<WalletPage />} />
          <Route path="recharge" element={<RechargePage />} />
          <Route path="consumption" element={<ConsumptionPage />} />

          {/* 数据统计 */}
          <Route path="statistics" element={<StatisticsPage />} />
          <Route path="statistics/transactions" element={<TransactionStatsPage />} />
          <Route path="statistics/profit-analysis" element={<ProfitAnalysisPage />} />
          <Route path="statistics/market-trends" element={<MarketTrendsPage />} />
        </Route>

        {/* 默认重定向 */}
        <Route path="*" element={
          !initialized ? fullPageLoading : <Navigate to={isAuthenticated ? "/" : "/auth/login"} replace />
        } />
      </Routes>
    </div>
  </ConfigProvider>
  );
}

export default App;
